import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const MissionControl = () => {
  const [activeValue, setActiveValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('mission-control');
    if (element) observer?.observe(element);

    return () => observer?.disconnect();
  }, []);

  const coreValues = [
    {
      title: "Innovation First",
      description: "We don't just follow trends—we create them. Every project is an opportunity to push boundaries and explore new possibilities in digital experiences.",
      icon: "Lightbulb",
      color: "accent"
    },
    {
      title: "Human-Centered Design",
      description: "Technology should serve people, not the other way around. We craft experiences that feel intuitive, accessible, and genuinely helpful to users.",
      icon: "Heart",
      color: "success"
    },
    {
      title: "Technical Excellence",
      description: "Quality code is our signature. We believe in clean architecture, scalable solutions, and maintainable systems that stand the test of time.",
      icon: "Code",
      color: "warning"
    },
    {
      title: "Collaborative Spirit",
      description: "Great software is built by great teams. We foster open communication, shared ownership, and collective problem-solving in everything we do.",
      icon: "Users",
      color: "blue-500"
    },
    {
      title: "Continuous Learning",
      description: "The tech landscape evolves rapidly, and so do we. We invest in our growth, experiment with new technologies, and share knowledge freely.",
      icon: "BookOpen",
      color: "purple-500"
    },
    {
      title: "Sustainable Impact",
      description: "We build solutions that create lasting positive change—for our clients, their users, and the broader digital ecosystem we're all part of.",
      icon: "Leaf",
      color: "green-500"
    }
  ];

  const philosophy = {
    mission: "To lift the shutter on digital possibilities by creating transformative software experiences that bridge the gap between complex technology and human needs.",
    vision: "A world where every digital interaction feels magical, intuitive, and purposeful—where technology amplifies human potential rather than complicating it.",
    approach: "We believe in the power of 3D storytelling, immersive experiences, and thoughtful engineering to turn ambitious ideas into reality."
  };

  return (
    <section id="mission-control" className="py-12 md:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Target" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Our Foundation</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Mission Control
          </h2>
          <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            The values, philosophy, and principles that guide every decision we make and every line of code we write.
          </p>
        </div>

        {/* Philosophy Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12 md:mb-16">
          {[
            { title: "Our Mission", content: philosophy.mission, icon: "Compass" },
            { title: "Our Vision", content: philosophy.vision, icon: "Eye" },
            { title: "Our Approach", content: philosophy.approach, icon: "Zap" }
          ].map((item, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-xl p-6"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4">
                <Icon name={item.icon} size={28} className="text-accent" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.content}</p>
            </div>
          ))}
        </div>

        {/* Core Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreValues.map((value, index) => (
            <div
              key={index}
              onMouseEnter={() => setActiveValue(index)}
              className={`bg-card border rounded-xl p-6 transition-all ${
                activeValue === index ? 'border-accent shadow-lg' : 'border-border'
              }`}
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4">
                <Icon name={value.icon} size={28} className="text-accent" />
              </div>

              <h3 className="text-lg font-bold text-foreground mb-3">{value.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>

        {/* Philosophy Visualization */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-block">
            <div className="w-24 h-24 border-2 border-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                <Icon name="Zap" size={28} className="text-accent" />
              </div>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
              Where Code Meets Creativity
            </h3>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              Every project is a canvas where technical precision meets creative vision, resulting in digital experiences that don't just work—they inspire.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionControl;